import React from 'react'
import {
  Col,
  Form,
  FormInternalNamePath,
  Input,
  PhoneInputValue,
  Row,
  getValueVsTemplateLength
} from '@commutifi-fe/ui'
import { isBoolean } from 'lodash'
import {
  AsYouType,
  CountryCode,
  examples,
  getExampleNumber,
  parsePhoneNumberWithError
} from '@commutifi-fe/phone-number'
import { TypedIntl, useComponentIntl } from '../../../locales'
import { PhoneNumberFormItem, PhoneNumberFormItemProps } from './PhoneNumber'

const getDescriptiveInvalidNumberError = (
  number: string,
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- Legacy
  countryCode: string | CountryCode,
  { intl, strict }: { intl: TypedIntl<'phone'>; strict: boolean }
) => {
  const { currentValueLength, templateLength, template } = getValueVsTemplateLength(number, countryCode)
  return currentValueLength === templateLength
    ? intl.formatMessage({ id: 'form.input.phoneNumber.error.invalid' })
    : strict || currentValueLength > templateLength
      ? intl.formatMessage({ id: 'form.input.phoneNumber.error.format' }, { template })
      : undefined
}

type ValidateFuncType = (
  value: PhoneInputValue,
  options: { intl: TypedIntl<'phone'>; strict?: boolean; required?: boolean }
) => Promise<void>
export const validatePhoneNumber: ValidateFuncType = (value, options) => {
  const { intl, strict, required } = options
  const strictValidation = isBoolean(strict) ? strict : true

  if (!value?.phone) {
    return !required ? Promise.resolve() : Promise.reject(new Error(intl.formatMessage({ id: 'required' })))
  }

  try {
    const phoneNumber = parsePhoneNumberWithError(`+${value.code} ${value.phone}`, value.short)
    if (phoneNumber?.isValid()) {
      return Promise.resolve()
    }
    if (!value.short && !value.code) {
      return Promise.reject(new Error(intl.formatMessage({ id: 'form.input.phoneNumber.error.invalidCountry' })))
    }

    const asYouType = new AsYouType(value.short as CountryCode)
    const ex = getExampleNumber(value.short as CountryCode, examples)
    asYouType.input(ex?.formatNational() || '')
    const errMessage = getDescriptiveInvalidNumberError(value.phone, value.short || 'US', {
      intl,
      strict: strictValidation
    })
    return errMessage ? Promise.reject(new Error(errMessage)) : Promise.resolve()
  } catch (e: any) {
    let errMessage
    switch (e.message) {
      case 'INVALID_COUNTRY':
        errMessage = intl.formatMessage({ id: 'form.input.phoneNumber.error.invalidCountry' })
        break
      case 'TOO_SHORT':
        if (strictValidation) {
          errMessage = intl.formatMessage({ id: 'form.input.phoneNumber.error.tooShort' })
        }
        break
      case 'TOO_LONG':
        errMessage = intl.formatMessage({ id: 'form.input.phoneNumber.error.tooLong' })
        break
      case 'NOT_A_NUMBER':
        errMessage = intl.formatMessage({ id: 'form.input.phoneNumber.error.notANumber' })
        break
      default: {
        if (!value.short && !value.code) {
          errMessage = intl.formatMessage({ id: 'form.input.phoneNumber.error.invalidCountry' })
          break
        }
        const asYouType = new AsYouType(value.short as CountryCode)
        const ex = getExampleNumber(value.short as CountryCode, examples)
        const nationalNumber = ex?.formatNational() || ''
        asYouType.input(nationalNumber)
        errMessage = getDescriptiveInvalidNumberError(value.phone, value.short || 'US', {
          intl,
          strict: strictValidation
        })
      }
    }

    return errMessage ? Promise.reject(new Error(errMessage)) : Promise.resolve()
  }
}

export interface PhoneNumberWithExtFormItemsProps extends PhoneNumberFormItemProps {
  /**
   * Form Item name: See antd Form.Item name prop
   */
  name?: string | number | FormInternalNamePath
  /**
   * If phone extension should be asked
   */
  showExt?: boolean

  /**
   * Inputs disabled state
   */
  disabled?: boolean
  /**
   * @defaultValue true
   */
  required?: boolean
}

/**
 * Preformatted phone input with optional extension input to display phone number
 * input(s) in an antd Form component.
 * Also comes with validation rules to check phone country dial code presence and
 * phone number proper format based on country code selected by teh user.
 *
 * See typescript interface [[FullPhoneNumberProps]] for more details on accepted props.
 */
export function PhoneNumberWithExtFormItems({
  name = 'phoneNumber',
  showExt = true,
  disabled,
  required = true,
  hidden,
  ...formItemProps
}: PhoneNumberWithExtFormItemsProps) {
  const intl = useComponentIntl('phone')
  return (
    <Row>
      <Col span={showExt ? 19 : 24}>
        <PhoneNumberFormItem
          name={name}
          rules={[
            {
              required,
              validateTrigger: 'onChange',
              validator: (_, value: PhoneInputValue) => validatePhoneNumber(value, { intl, strict: false })
            },
            {
              validateTrigger: 'onBlur',
              validator: (_, value: PhoneInputValue) => validatePhoneNumber(value, { intl, strict: true, required })
            }
          ]}
          hidden={hidden}
          {...formItemProps}
        >
          <PhoneNumberFormItem.TelInput disabled={disabled} />
        </PhoneNumberFormItem>
      </Col>
      {showExt ? (
        <Col offset={1} span={4}>
          <Form.Item
            name={typeof name === 'string' || typeof name === 'number' ? [name, 'ext'] : [...name, 'ext']}
            label={intl.formatMessage({ id: 'label.phoneNumber.extension' })}
            hidden={hidden}
          >
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      ) : null}
    </Row>
  )
}
